import Spinner from '@alexis/components/Spinner';
import { clsx } from '@alexis/helpers/clsx';
import { appendSearchParams } from '@alexis/helpers/searchParam';
import axios, { CancelTokenSource } from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Subscription } from 'rxjs';

import { getMapPoi, getMapPoiDetail } from '@apis/places';

import AlertMessage, { MessageTypes } from '@components/AlertMessage/AlertMessage';

import { MAX_RADIUS_IN_KM, NUMBER_OF_HOTEL_MARKERS } from '@constants/hotels';

import { injectGoogleMapScript } from '@helpers/googleMap';
import { convertGuestSearchParamToGuestRooms } from '@helpers/hotel';
import { getHotelPriceByRating } from '@helpers/hotel';
import { getReviewRatingType } from '@helpers/review';

import ChevronRight from '@icons/chevron-right-small.svg';
import ChevronLeft from '@icons/chevron_left.svg';
import ClearIcon from '@icons/close.svg';
import DoubleArrowRightIcon from '@icons/double_arrow_right.svg';
import SearchIcon from '@icons/search.svg';

import { useExtraParams } from '@hooks/useExtraParams';
import { useViewportSize } from '@hooks/useViewportSize';

import { hotelSearchObservable } from '@observables/hotelObservables';
import { setHotelHovered, useHoverFlag } from '@observables/mapObservables';

import { hotelSearchResultObserver } from '@observers/hotelObserver';

import { resetHotelMetaSearch } from '@redux/actions/hotelMetaSearchActions';
import {
  getCurrencyState,
  getCurrentSiteState,
  getExchangeRateState,
  getHotelMetaSearchState,
  getIsRtlState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import { CurrentSite } from '@wegoTypes/currentSite';
import { HotelLandmark } from '@wegoTypes/hotels/hotelLandmarks';
import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';
import { HotelViewModel } from '@wegoTypes/hotels/hotelViewModel';
import { Prediction, Predictions } from '@wegoTypes/hotels/metasearch/hotelMapSearchPoi';
import { HotelMetaSearchHotelReview } from '@wegoTypes/hotels/metasearch/hotelMetaSearchHotelReview';
import { HotelSearchResultHotel } from '@wegoTypes/hotels/metasearch/hotelSearchResultHotel';
import { RateViewModel } from '@wegoTypes/hotels/rateViewModel';
import { Translations } from '@wegoTypes/translations';

import HotelCardMap from '../HotelCardMap/HotelCardMap';
import styles from './HotelMap.module.scss';

interface HotelMapProps {
  hotelViewModels: Array<HotelViewModel>;
  className?: string;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  isLoadingCitySearch?: boolean;
  isMapFullscreen?: boolean;
  onApplyMap: (googleMap: google.maps.Map) => void;
  onHotelCardMapClick: (hotelId?: number) => void;
  setLoading: (loading: boolean) => void;
  onCloseMap?: () => void;
  setMapFullscreen?: (isFullscreen: boolean) => void;
}

const HotelMap: React.FC<HotelMapProps> = ({
  className,
  hotelViewModels,
  onApplyMap,
  onHotelCardMapClick,
  onCloseMap,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  isLoadingCitySearch,
  isMapFullscreen,
  setLoading,
  setMapFullscreen,
}): JSX.Element => {
  const apiBaseUrl: string = API_BASE_URL;
  const currency: Currency = useSelector(getCurrencyState);
  const currentSite: CurrentSite = useSelector(getCurrentSiteState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);
  const hotelMetaSearch: HotelMetaSearch = useSelector(getHotelMetaSearchState);
  const isRtl: boolean = useSelector(getIsRtlState);
  const locale: string = useSelector(getLocaleState);
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const translations: Translations = useSelector(getTranslationsState);

  const placePickerRef = useRef<HTMLInputElement>(null);
  const landmarksRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const { pathname, search } = useLocation();
  const { checkInDate, checkOutDate, districtId } = useParams();
  const { extraBodyParams, extraSearchBodyParams } = useExtraParams();
  const [searchParams] = useSearchParams();
  const { width } = useViewportSize();
  const isHalfScreenMap = width > 1208;

  const guestsSearchParam = searchParams.get('guests');
  const radiusLatParam = searchParams.get('lat');
  const radiusLongParam = searchParams.get('long');
  const { hotelHovered } = useHoverFlag();

  const mapDivRef = useRef<HTMLDivElement>(null);
  const hotelCardPopUpRef = useRef<HTMLDivElement>(null);
  const hasMoved = useRef(false);
  const keepMapViewport = useRef(false);
  const previousHoveredMarker = useRef<google.maps.Marker>();
  const radiusSearchSub = useRef<Subscription>();

  const [googleMap, setGoogleMap] = useState<google.maps.Map>();
  const [googleMapMarkers, setGoogleMapMarkers] = useState<Array<google.maps.Marker>>([]);
  const [previousHotelMarkers, setPreviousHotelMarkers] = useState<Array<google.maps.Marker>>([]);
  const [previousPOIMarkers, setPreviousPOIMarkers] = useState<Array<google.maps.Marker>>([]);
  const [selectedMarkerHotelId, setSelectedMarkerHotelId] = useState<number>();
  const [gInfoWindow, setGoogleInfoWindow] = useState<google.maps.InfoWindow>();
  const [selectedPOI, setSelectedPOI] = useState<{ lat?: number; long?: number; name?: string }>(
    {},
  );

  const [radiusSearchClicked, setRadiusSearchClicked] = useState<boolean>(false);
  const [landmarks, setLandmarks] = useState<HotelLandmark[]>([]);
  const [isStartSearchPOI, setIsStartSearchPOI] = useState<boolean>(false);
  const [isPlacePickerFocus, setIsPlacePickerFocus] = useState<boolean>(false);
  const [destinationSearchQuery, setDestinationSearchQuery] = useState<string>('');
  const [selectedPlace, setSelectedPlace] = useState<Prediction>();
  const [destinationSearchQueryPlaces, setDestinationSearchQueryPlaces] = useState<Predictions>({
    predictions: [],
    sessionToken: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [landmarkScroll, setLandmarkScroll] = useState(0);
  const [indexDisplayPlaceholder, setIndexDisplayPlaceholder] = useState<number>(0);

  const [originalSearchId, setOriginalSearchId] = useState<string>();
  const selectedMarkerHotelViewModel = useMemo<HotelViewModel | undefined>(() => {
    if (hotelViewModels.length > 0) {
      return hotelViewModels.find(
        (hotelViewModel) => hotelViewModel.hotelId === selectedMarkerHotelId,
      );
    }
    return undefined;
  }, [selectedMarkerHotelId, hotelViewModels.length]);

  const priceDisplayedMarker = (currency: Currency, price: string) =>
    currency.symbolFirst ? `${currency.symbol} ${price}` : `${price} ${currency.symbol}`;

  const landmarkScrollWidth =
    (landmarksRef.current?.scrollWidth || 0) - (landmarksRef.current?.clientWidth || 0);

  const searchWithoutExtraParams = useMemo<string>(() => {
    const searchParams = new URLSearchParams(search);
    [
      'modal',
      'sort',
      'order',
      'name',
      'deals',
      'amenities',
      'brands',
      'chains',
      'districts',
      'price',
      'accommodation_types',
      'guest_rating',
      'guest_score',
      'stars',
      'open_calendar',
      'selected_hotel_ids',
      'view',
    ].map((item) => searchParams.delete(item));

    const updatedSearchParams = searchParams.toString();

    return !!updatedSearchParams ? `?${updatedSearchParams}` : '';
  }, [search]);

  const getLandmarkIcon = useCallback(() => {
    const svg = (
      <svg
        width='66'
        height='71'
        viewBox='0 0 66 71'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <g filter='url(#filter0_d_8875_115108)'>
          <g filter='url(#filter1_d_8875_115108)'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M33 6.5C19.7452 6.5 9 17.2452 9 30.5C9 41.2129 16.019 50.2864 25.7091 53.3725C25.8647 53.4221 26.0073 53.5073 26.1228 53.6228L32.1161 59.6161C32.6043 60.1043 33.3957 60.1043 33.8839 59.6161L39.8772 53.6228C39.9927 53.5073 40.1353 53.4221 40.2909 53.3725C49.981 50.2864 57 41.2129 57 30.5C57 17.2452 46.2548 6.5 33 6.5Z'
              fill='#44B50C'
            />
            <path
              d='M32.1161 59.6161L31.7626 59.9697L32.1161 59.6161ZM33.8839 59.6161L33.5303 59.2626L33.8839 59.6161ZM39.8772 53.6228L40.2308 53.9763L39.8772 53.6228ZM40.2909 53.3725L40.4426 53.8489L40.2909 53.3725ZM25.7091 53.3725L25.8608 52.8961L25.7091 53.3725ZM26.1228 53.6228L26.4763 53.2692L26.1228 53.6228ZM9.5 30.5C9.5 17.5213 20.0213 7 33 7V6C19.469 6 8.5 16.969 8.5 30.5H9.5ZM25.8608 52.8961C16.3722 49.8741 9.5 40.9889 9.5 30.5H8.5C8.5 41.4368 15.6659 50.6986 25.5574 53.8489L25.8608 52.8961ZM32.4697 59.2626L26.4763 53.2692L25.7692 53.9763L31.7626 59.9697L32.4697 59.2626ZM33.5303 59.2626C33.2374 59.5555 32.7626 59.5555 32.4697 59.2626L31.7626 59.9697C32.446 60.6531 33.554 60.6531 34.2374 59.9697L33.5303 59.2626ZM39.5237 53.2692L33.5303 59.2626L34.2374 59.9697L40.2308 53.9763L39.5237 53.2692ZM56.5 30.5C56.5 40.9889 49.6278 49.8741 40.1392 52.8961L40.4426 53.8489C50.3341 50.6986 57.5 41.4368 57.5 30.5H56.5ZM33 7C45.9787 7 56.5 17.5213 56.5 30.5H57.5C57.5 16.969 46.531 6 33 6V7ZM40.2308 53.9763C40.2878 53.9193 40.3604 53.8751 40.4426 53.8489L40.1392 52.8961C39.9102 52.969 39.6977 53.0952 39.5237 53.2692L40.2308 53.9763ZM25.5574 53.8489C25.6396 53.8751 25.7122 53.9193 25.7692 53.9763L26.4763 53.2692C26.3023 53.0952 26.0898 52.969 25.8608 52.8961L25.5574 53.8489Z'
              fill='#44B50C'
            />
          </g>
        </g>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M32.4855 19.6425C32.8022 19.4525 33.1978 19.4525 33.5145 19.6425L43.5145 25.6425C43.8157 25.8232 44 26.1487 44 26.5V29.5C44 30.0523 43.5523 30.5 43 30.5H42V36.5H43C43.5523 36.5 44 36.9477 44 37.5V40.5C44 41.0523 43.5523 41.5 43 41.5H23C22.4477 41.5 22 41.0523 22 40.5V37.5C22 36.9477 22.4477 36.5 23 36.5H24V30.5H23C22.4477 30.5 22 30.0523 22 29.5V26.5C22 26.1487 22.1843 25.8232 22.4855 25.6425L32.4855 19.6425ZM34 30.5H36V36.5H34V30.5ZM38 36.5V30.5H40V36.5H38ZM28 30.5H26V36.5H28V30.5ZM30 30.5H32V36.5H30V30.5ZM42 27.5V28.5H24V27.5H42ZM26.6103 25.5L33 21.6662L39.3897 25.5H26.6103ZM24 38.5V39.5H42V38.5H24Z'
          fill='white'
        />
        <defs>
          <filter
            id='filter0_d_8875_115108'
            x='0.5'
            y='0'
            width='65'
            height='70.4822'
            filterUnits='userSpaceOnUse'
            colorInterpolationFilters='sRGB'
          >
            <feFlood floodOpacity='0' result='BackgroundImageFix' />
            <feColorMatrix
              in='SourceAlpha'
              type='matrix'
              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
              result='hardAlpha'
            />
            <feOffset dy='2' />
            <feGaussianBlur stdDeviation='4' />
            <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0' />
            <feBlend
              mode='normal'
              in2='BackgroundImageFix'
              result='effect1_dropShadow_8875_115108'
            />
            <feBlend
              mode='normal'
              in='SourceGraphic'
              in2='effect1_dropShadow_8875_115108'
              result='shape'
            />
          </filter>
          <filter
            id='filter1_d_8875_115108'
            x='0.5'
            y='0'
            width='65'
            height='70.4822'
            filterUnits='userSpaceOnUse'
            colorInterpolationFilters='sRGB'
          >
            <feFlood floodOpacity='0' result='BackgroundImageFix' />
            <feColorMatrix
              in='SourceAlpha'
              type='matrix'
              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
              result='hardAlpha'
            />
            <feOffset dy='2' />
            <feGaussianBlur stdDeviation='4' />
            <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0' />
            <feBlend
              mode='normal'
              in2='BackgroundImageFix'
              result='effect1_dropShadow_8875_115108'
            />
            <feBlend
              mode='normal'
              in='SourceGraphic'
              in2='effect1_dropShadow_8875_115108'
              result='shape'
            />
          </filter>
        </defs>
      </svg>
    );

    return window.btoa(renderToString(svg));
  }, []);

  const handleRadiusSearch = useCallback(
    (googleMap: google.maps.Map) => {
      if (!!googleMap) {
        if (radiusSearchSub.current) {
          radiusSearchSub.current.unsubscribe();
        }
        const mapCenter = googleMap.getCenter();
        const mapBounds = googleMap.getBounds();
        const northEast = mapBounds?.getNorthEast();
        const boundCenter = mapBounds?.getCenter();

        const northPoint = new google.maps.LatLng(northEast?.lat() || 0, boundCenter?.lng());
        const eastPoint = new google.maps.LatLng(boundCenter?.lat() || 0, northEast?.lng());
        const radiusToNorth =
          google.maps.geometry.spherical.computeDistanceBetween(mapCenter!, northPoint) / 1000;
        const radiusToEast =
          google.maps.geometry.spherical.computeDistanceBetween(mapCenter!, eastPoint) / 1000;
        const distance = Math.min(MAX_RADIUS_IN_KM, Math.max(radiusToNorth, radiusToEast) / 2);
        const guestRooms = convertGuestSearchParamToGuestRooms(guestsSearchParam!);

        const params = {
          url: `${apiBaseUrl}/v3/metasearch/hotels/searches`,
          user: null,
          search: {
            originalSearchId: originalSearchId,
            siteCode: currentSite.countryCode,
            locale,
            currencyCode: currency.code,
            checkIn: checkInDate!,
            checkOut: checkOutDate!,
            rooms: guestRooms,
            longitude: mapCenter?.lng(),
            latitude: mapCenter?.lat(),
            radius: distance,
          },
          clientId: wegoAnalyticsClientId,
          sessionId: wegoAnalyticsClientSessionId,
          extraSearchBodyParams,
          extraBodyParams,
        };
        dispatch(resetHotelMetaSearch());

        setLoading(true);

        const hotelSearchObservable$ = hotelSearchObservable(params);
        setSelectedPOI({});

        radiusSearchSub.current = hotelSearchObservable$.subscribe(
          hotelSearchResultObserver(
            dispatch,
            priceOptions,
            () => {
              setLoading(false);
            },
            (err) => {
              if (err) {
                setLoading(false);
              }
            },
          ),
        );

        return () => {
          radiusSearchSub.current?.unsubscribe();
        };
      }
    },
    [originalSearchId],
  );

  const createRadiusSearchControl = (googleMap: google.maps.Map) => {
    const controlButton = document.createElement('button');

    controlButton.id = 'map-control-button';

    controlButton.onclick = () => {
      handleRadiusSearch(googleMap);
      setRadiusSearchClicked(true);
      hasMoved.current = false;
      keepMapViewport.current = true;
    };

    controlButton.type = 'button';

    return controlButton;
  };

  const createCloseMapControl = () => {
    const controlButton = document.createElement('button');
    const tooltip = document.createElement('div');

    controlButton.id = 'close-map-button';
    tooltip.classList.add('close-tooltip');

    if (isRtl) {
      controlButton.style.marginLeft = '24px';
    }
    controlButton.style.marginRight = '24px';

    controlButton.onclick = () => onCloseMap && onCloseMap();
    controlButton.onmouseover = () => {
      tooltip.innerHTML = `
      <div>${translations.close_map}</div>
      <svg class="tooltip-arrow" width="13" height="8" viewBox="0 0 13 8" fill="#1D1D1D">
        <path d="M5.7 1.06667C6.1 0.533333 6.9 0.533333 7.3 1.06667L12.5 8L0.5 8L5.7 1.06667Z" />
      </svg>
      `;
      controlButton.append(tooltip);
    };
    controlButton.onmouseleave = () => {
      controlButton.removeChild(tooltip);
    };
    controlButton.innerHTML = `
    <svg title="Close map" width="16" height="16" viewBox="0 0 16 16" fill="#1D1D1D">
      <path d="M8 8.94281L3.13807 13.8047C2.87772 14.0651 2.45561 14.0651 2.19526 13.8047C1.93491 13.5444 1.93491 13.1223 2.19526 12.8619L7.05719 8L2.19526 3.13807C1.93491 2.87772 1.93491 2.45561 2.19526 2.19526C2.45561 1.93491 2.87772 1.93491 3.13807 2.19526L8 7.05719L12.8619 2.19526C13.1223 1.93491 13.5444 1.93491 13.8047 2.19526C14.0651 2.45561 14.0651 2.87772 13.8047 3.13807L8.94281 8L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L8 8.94281Z" />
    </svg>`;

    controlButton.type = 'button';

    return controlButton;
  };

  const getBase64MarkerIcon = (review: HotelMetaSearchHotelReview, hotelPrice: string) => {
    let markerColor = '#767676'; // $neutral-600

    if (!!review) {
      switch (getReviewRatingType(review.score)) {
        case 'EXCELLENT':
        case 'VERYGOOD':
        case 'GOOD':
          markerColor = '#44b50c'; // $green-400
          break;
        case 'PLEASANT':
        case 'FAIR':
          markerColor = '#ff9800'; // $orange-300
          break;
        case 'POOR':
          markerColor = '#fa4c4c'; // $red-300
          break;
      }
    }
    const svg = (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        height={30}
        width={hotelPrice.length * 11}
        viewBox={`0 0 ${hotelPrice.length * 11} 30`}
      >
        <path
          d='M12 20c4.41 0 8-3.59 8-8s-3.59-8-8-8-8 3.59-8 8 3.59 8 8 8z'
          fill='transparent'
        ></path>
        <path
          d='M12 22c5.52 0 10-4.48 10-10S17.52 2 12 2 2 6.48 2 12s4.48 10 10 10zm0-18c4.41 0 8 3.59 8 8s-3.59 8-8 8-8-3.59-8-8 3.59-8 8-8z'
          fill='transparent'
          filter={`drop-shadow(0 0 2px ${markerColor})`}
        ></path>
      </svg>
    );

    return window.btoa(renderToString(svg));
  };

  const rateHandoffHref = useCallback<(rate: RateViewModel) => string>(
    (rate: RateViewModel) => {
      return appendSearchParams(rate.handoffUrl, {
        wc: wegoAnalyticsClientId,
        ws: wegoAnalyticsClientSessionId,
      });
    },
    [wegoAnalyticsClientId, wegoAnalyticsClientSessionId],
  );

  const hotelDetailHref = useCallback<(hotel: HotelSearchResultHotel) => string>(
    (hotel: HotelSearchResultHotel) => {
      const pathname$ = !!districtId ? pathname.replace(`/d${districtId}`, '') : pathname;

      return appendSearchParams(
        `${window.location.origin}${pathname$}/${hotel.id}${searchWithoutExtraParams}`,
        { search_id: hotelMetaSearch?.search?.id },
      );
    },
    [pathname, districtId, searchWithoutExtraParams, hotelMetaSearch?.search?.id],
  );

  const registerMarkerEventListener = useCallback(
    (googleMarker, googleInfoWindow, hotelViewModel: HotelViewModel) => {
      // open info window(show hotelCardMap)
      googleMarker.addListener('click', () => {
        setHotelHovered && setHotelHovered(undefined);
        window.open(
          !!hotelViewModel.sponsoredAd
            ? rateHandoffHref(hotelViewModel.filteredRates[0] || undefined)
            : hotelDetailHref(hotelViewModel.hotel),
          '_blank',
          'noreferrer',
        );
        setSelectedMarkerHotelId(hotelViewModel.hotelId);
      });

      googleMarker.addListener('mouseover', () => {
        setHotelHovered && setHotelHovered(undefined);
        googleInfoWindow.open({
          anchor: googleMarker,
          map: googleMap,
        });
        setSelectedMarkerHotelId(hotelViewModel.hotelId);
      });

      googleInfoWindow.addListener('closeclick', () => {
        setHotelHovered && setHotelHovered(undefined);
      });
    },
    [setHotelHovered, setSelectedMarkerHotelId],
  );

  const handlePoiClick = (lat: number, long: number, name: string) => {
    if (radiusSearchSub.current) {
      radiusSearchSub.current.unsubscribe();
    }
    setLoading(true);
    previousPOIMarkers.forEach((marker) => marker.setMap(null));
    hasMoved.current = false;
    keepMapViewport.current = false;
    setSelectedPOI({ lat, long, name });
    setRadiusSearchClicked(false);

    const guestRooms = convertGuestSearchParamToGuestRooms(guestsSearchParam!);
    if (googleMap) {
      const params = {
        url: `${apiBaseUrl}/v3/metasearch/hotels/searches`,
        user: null,
        search: {
          originalSearchId,
          siteCode: currentSite.countryCode,
          locale,
          currencyCode: currency.code,
          checkIn: checkInDate!,
          checkOut: checkOutDate!,
          rooms: guestRooms,
          longitude: long,
          latitude: lat,
          radius: 2,
        },
        clientId: wegoAnalyticsClientId,
        sessionId: wegoAnalyticsClientSessionId,
        extraSearchBodyParams,
        extraBodyParams,
      };
      dispatch(resetHotelMetaSearch());

      const hotelSearchObservable$ = hotelSearchObservable(params);

      const landmarkBase64 = getLandmarkIcon();
      const POIMarker = new google.maps.Marker({
        icon: {
          url: `data:image/svg+xml;base64,${landmarkBase64}`,
        },
        optimized: true,
        position: new google.maps.LatLng({
          lat: lat,
          lng: long,
        }),
        animation: google.maps.Animation.BOUNCE,
        zIndex: google.maps.Marker.MAX_ZINDEX - 1,
      });
      POIMarker.setMap(googleMap);
      setPreviousPOIMarkers([POIMarker]);

      radiusSearchSub.current = hotelSearchObservable$.subscribe(
        hotelSearchResultObserver(
          dispatch,
          priceOptions,
          () => {
            setLoading(false);
          },
          (err) => {
            if (err) {
              setLoading(false);
            }
          },
        ),
      );

      return () => {
        radiusSearchSub.current?.unsubscribe();
      };
    }
  };

  const handlePoiSearch = () => {
    setIsStartSearchPOI(true);
  };

  const handleCloseSearchLayout = () => {
    setDestinationSearchQuery('');
    setSelectedPlace(undefined);
    setIsStartSearchPOI(false);
  };

  const handleClearSearchQuery = () => {
    setDestinationSearchQuery('');
    setSelectedPlace(undefined);
  };

  const handleOnPlacePickerFocus = useCallback(() => {
    setIsPlacePickerFocus(true);

    placePickerRef.current!.select();
  }, [placePickerRef]);

  const handleCloseInfoWindow = () => {
    gInfoWindow?.close();
    setSelectedMarkerHotelId(undefined);
  };

  const handleOnPlacePickerChange = useCallback<(value: string) => void>((value: string) => {
    setDestinationSearchQuery(value);
    setSelectedPlace(undefined);
  }, []);

  const handleOnPlaceSelected = useCallback<(place: Prediction) => void>((place: Prediction) => {
    setSelectedPlace(place);
    setIsPlacePickerFocus(false);
  }, []);

  const handleOnPlacePickerKeyDown = useCallback<
    (event: React.KeyboardEvent<HTMLInputElement>) => void
  >(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();

        if (!!event.currentTarget.value && destinationSearchQueryPlaces.predictions.length > 0) {
          setSelectedPlace(destinationSearchQueryPlaces.predictions[0]);

          setIsPlacePickerFocus(false);
          placePickerRef.current!.blur();
        }
      }
    },
    [placePickerRef, destinationSearchQueryPlaces],
  );

  const getPOIDisplay = useCallback<(place: Prediction) => JSX.Element>(
    (place: Prediction) => {
      return (
        <>
          <i className={clsx(styles.icon, styles.districtGrey)}></i>
          <div className={styles.labelAndHotelCount}>
            <div>{place.description}</div>
          </div>
        </>
      );
    },
    [translations],
  );

  useEffect(() => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
    let getPlaceTimeout: NodeJS.Timeout | undefined;

    if (!!selectedPlace) {
      setDestinationSearchQuery(selectedPlace.description);
      setIsStartSearchPOI(false);

      getPlaceTimeout = setTimeout(async (): Promise<void> => {
        try {
          const landMarkDetail = await getMapPoiDetail(
            apiBaseUrl,
            selectedPlace.placeId,
            cancelTokenSource.token,
          );
          handlePoiClick(landMarkDetail.latitude, landMarkDetail.longitude, landMarkDetail.name);
        } catch (error) {
          setDestinationSearchQueryPlaces({ predictions: [], sessionToken: '' });
        }
      }, 150);
    } else {
      cancelTokenSource.cancel('Empty search query.');
      setDestinationSearchQueryPlaces({ predictions: [], sessionToken: '' });
    }

    return () => {
      cancelTokenSource.cancel('Component unmount.');

      if (!!getPlaceTimeout) {
        clearTimeout(getPlaceTimeout);
      }
    };
  }, [selectedPlace]);

  useEffect(() => {
    if (isStartSearchPOI && placePickerRef.current) {
      placePickerRef.current.focus();
      setIsPlacePickerFocus(true);
    }
  }, [isStartSearchPOI]);

  // Get POI based on destinationSearchQuery
  useEffect(() => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
    let getPlaceTimeout: NodeJS.Timeout | undefined;
    const center = googleMap && googleMap.getCenter();

    if (!!destinationSearchQuery && isPlacePickerFocus && !selectedPlace && center) {
      getPlaceTimeout = setTimeout(async (): Promise<void> => {
        try {
          setIsLoading(true);
          const places = await getMapPoi(
            apiBaseUrl,
            locale,
            destinationSearchQuery,
            center.lng(),
            center.lat(),
            cancelTokenSource.token,
          );
          setDestinationSearchQueryPlaces(places);
          setIsLoading(false);
        } catch (error: any) {
          setDestinationSearchQueryPlaces({ predictions: [], sessionToken: '' });
          if (error.message !== 'Cancel request') {
            setIsLoading(false);
          }
        }
      }, 150);
    } else {
      cancelTokenSource.cancel('Empty search query.');
      setDestinationSearchQueryPlaces({ predictions: [], sessionToken: '' });
    }

    return () => {
      cancelTokenSource.cancel('Cancel request');

      if (!!getPlaceTimeout) {
        clearTimeout(getPlaceTimeout);
      }
    };
  }, [destinationSearchQuery, locale, isPlacePickerFocus, selectedPlace, googleMap]);

  // Initialize google map
  useEffect(() => {
    if (!!mapDivRef && !!mapDivRef.current) {
      const initializeGoogleMap = async (locale: string): Promise<void> => {
        await injectGoogleMapScript(locale);

        const mapOptions: google.maps.MapOptions = {
          clickableIcons: false,
          fullscreenControl: false,
          keyboardShortcuts: false,
          mapTypeControl: false,
          maxZoom: 18,
          minZoom: 12,
          streetViewControl: false,
          styles: [
            {
              featureType: 'administrative',
              elementType: 'geometry',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'administrative.land_parcel',
              elementType: 'labels',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'landscape',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'poi',
              elementType: 'labels.text',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'poi.business',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'poi.attraction',
              elementType: 'labels.text',
              stylers: [
                {
                  visibility: 'on',
                },
              ],
            },
            {
              featureType: 'poi.sports_complex',
              elementType: 'labels.text',
              stylers: [
                {
                  visibility: 'on',
                },
              ],
            },
            {
              featureType: 'road.local',
              elementType: 'labels',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
          ],
          zoom: 10,
          zoomControl: true,
          zoomControlOptions: {
            position: isRtl
              ? google.maps.ControlPosition.LEFT_TOP
              : google.maps.ControlPosition.RIGHT_TOP,
          },
          cameraControl: false,
        };

        const googleMap = new google.maps.Map(mapDivRef.current!, mapOptions);

        const radiusSearchControlDiv = document.createElement('div');
        const radiusSearchControl = createRadiusSearchControl(googleMap);
        radiusSearchControlDiv.appendChild(radiusSearchControl);

        googleMap.controls[google.maps.ControlPosition.TOP_CENTER].push(radiusSearchControlDiv);

        const closeMapDiv = document.createElement('div');
        const closeMapControl = createCloseMapControl();
        closeMapDiv.appendChild(closeMapControl);

        googleMap.controls[
          isRtl ? google.maps.ControlPosition.TOP_LEFT : google.maps.ControlPosition.TOP_RIGHT
        ].push(closeMapDiv);

        setGoogleMap(googleMap);
        onApplyMap(googleMap);
      };

      initializeGoogleMap(locale);
    }
  }, [mapDivRef, locale, isRtl, translations, originalSearchId]);

  // Render markers and show info window on map
  useEffect(() => {
    if (
      !!googleMap &&
      hotelViewModels.length > 0 &&
      !!hotelCardPopUpRef &&
      !!hotelCardPopUpRef.current
    ) {
      const hotelLatLngBounds = new google.maps.LatLngBounds();

      const googleInfoWindow = new google.maps.InfoWindow({
        content: hotelCardPopUpRef.current!,
        pixelOffset: new google.maps.Size(0, 190),
      });

      setGoogleInfoWindow(googleInfoWindow);

      const googleMarkers = hotelViewModels
        .slice(0, NUMBER_OF_HOTEL_MARKERS)
        .map((sortedAndFilteredHotelViewModel, index: number) => {
          const hotelLatLng = new google.maps.LatLng({
            lat: sortedAndFilteredHotelViewModel.hotel.latitude,
            lng: sortedAndFilteredHotelViewModel.hotel.longitude,
          });

          if (index < 5) hotelLatLngBounds.extend(hotelLatLng);

          const hotelPrice = getHotelPriceByRating(
            sortedAndFilteredHotelViewModel.filteredRates[0],
            priceOptions,
            currency,
            exchangeRate,
            locale,
          );

          // Custom marker icon with reviews
          const base64Svg = getBase64MarkerIcon(
            sortedAndFilteredHotelViewModel.hotel.reviews[0],
            priceDisplayedMarker(currency, hotelPrice),
          );

          const label = {
            text: priceDisplayedMarker(currency, hotelPrice),
            className: styles.hotelLabel,
            color: '#1D1D1D',
            fontSize: '16px',
          };

          const googleMarker = new google.maps.Marker({
            ...{ label },
            icon: {
              url: `data:image/svg+xml;base64,${base64Svg}`,
            },
            optimized: true,
            position: hotelLatLng,
            zIndex: google.maps.Marker.MAX_ZINDEX,
          });

          registerMarkerEventListener(
            googleMarker,
            googleInfoWindow,
            sortedAndFilteredHotelViewModel,
          );

          return googleMarker;
        });

      !keepMapViewport.current && !selectedPOI.lat && googleMap.fitBounds(hotelLatLngBounds);

      if (selectedPOI.lat && selectedPOI.long) {
        googleMap.setZoom(15);
        googleMap.setCenter(new google.maps.LatLng(selectedPOI.lat, selectedPOI.long));
      }

      if (radiusLatParam && radiusLongParam) {
        googleMap.setZoom(15);
        googleMap.setCenter(
          new google.maps.LatLng(parseFloat(radiusLatParam), parseFloat(radiusLongParam)),
        );
      }

      if (!keepMapViewport.current) {
        googleMap.setCenter(hotelLatLngBounds.getCenter());
      }

      setGoogleMapMarkers(googleMarkers);

      return () => {
        setGoogleMapMarkers([]);
        setSelectedMarkerHotelId(undefined);
      };
    }
  }, [
    googleMap,
    hotelViewModels.length,
    hotelCardPopUpRef,
    currency,
    priceOptions,
    exchangeRate,
    setHotelHovered,
    registerMarkerEventListener,
    selectedPOI,
    radiusLatParam,
    radiusLongParam,
  ]);

  // Show info window on map by hovered hotel in hotel list
  useEffect(() => {
    const currentMarkers = [...googleMapMarkers, ...previousHotelMarkers].find(
      (marker) =>
        marker.getPosition()?.lat() === hotelHovered?.hotel.latitude &&
        marker.getPosition()?.lng() === hotelHovered?.hotel.longitude,
    );

    if (!hotelHovered && previousHoveredMarker.current) {
      setPreviousHotelMarkers([...previousHotelMarkers, previousHoveredMarker.current]);
      if (previousHoveredMarker.current.getLabel()) {
        previousHoveredMarker.current.setLabel({
          ...(previousHoveredMarker.current.getLabel() as google.maps.MarkerLabel),
          className: styles.hotelLabel,
        });
      }
      return;
    }

    if (gInfoWindow && googleMap && hotelHovered && hotelHovered.filteredRates[0]) {
      const hotelPrice = getHotelPriceByRating(
        hotelHovered.filteredRates[0],
        priceOptions,
        currency,
        exchangeRate,
        locale,
      );
      const base64Svg = getBase64MarkerIcon(hotelHovered.hotel.reviews[0], hotelPrice);
      const foundMarker = currentMarkers
        ? currentMarkers
        : new google.maps.Marker({
            icon: {
              url: `data:image/svg+xml;base64,${base64Svg}`,
            },
            optimized: true,
            position: new google.maps.LatLng({
              lat: hotelHovered.hotel.latitude,
              lng: hotelHovered.hotel.longitude,
            }),
          });
      const label = {
        text: priceDisplayedMarker(currency, hotelPrice),
        className: styles.hotelLabelHover,
        fontSize: '16px',
      };

      foundMarker.setLabel(label);
      foundMarker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
      setSelectedMarkerHotelId(hotelHovered.hotelId);

      previousHoveredMarker.current = foundMarker;
      registerMarkerEventListener(foundMarker, gInfoWindow, hotelHovered);
      foundMarker.setMap(googleMap);

      if (!googleMap.getBounds()?.contains(foundMarker.getPosition()!)) {
        googleMap.panTo({
          lat: hotelHovered.hotel.latitude,
          lng: hotelHovered.hotel.longitude,
        });
      }
    }
  }, [
    gInfoWindow,
    googleMap,
    googleMapMarkers,
    hotelHovered,
    hotelHovered?.hotel.latitude,
    hotelHovered?.hotel.longitude,
    setSelectedMarkerHotelId,
    registerMarkerEventListener,
  ]);

  //Render marker clusters on map
  useEffect(() => {
    if (!!googleMap && googleMapMarkers.length > 0) {
      previousHotelMarkers.forEach((marker) => marker.setMap(null));
      googleMapMarkers.forEach((marker) => {
        marker.setMap(googleMap);
      });
      setPreviousHotelMarkers(googleMapMarkers);
    }
  }, [googleMap, googleMapMarkers]);

  // Show radius search button on map when user interact with map
  useEffect(() => {
    if (googleMap) {
      const RadiusSearchButton = `
      <svg fill="none" viewBox="0 0 24 24" width="24px" height="24px">
        <path fill-rule="evenodd" clip-rule="evenodd"
          d="M3.484 14H9a1 1 0 110 2H5.07a8.003 8.003 0 0014.474-1.33 1 1 0 111.886.666A10.003 10.003 0 014 18.003V21a1 1 0 11-2 0v-6a1 1 0 011-1h.484zm17.055-4a.99.99 0 01-.102 0H15a1 1 0 110-2h3.93A8.003 8.003 0 004.456 9.333a1 1 0 11-1.886-.666A10.003 10.003 0 0120 5.997V3a1 1 0 112 0v6a1 1 0 01-1 1h-.46z"
          fill='currentColor' />
      </svg>
      ${translations.search_this_area}`;

      googleMap.addListener('mousemove', () => {
        hasMoved.current = true;
      });
      googleMap.addListener('mouseout', () => {
        hasMoved.current = false;
      });

      googleMap.addListener('bounds_changed', () => {
        if (!!hasMoved.current) {
          keepMapViewport.current = true;
          setRadiusSearchClicked(false);

          const controlButton = document.getElementById('map-control-button');

          if (controlButton) {
            controlButton.style.visibility = radiusSearchClicked ? 'hidden' : 'visible';
            controlButton.innerHTML = RadiusSearchButton;
          }
        }
      });
    }
  }, [googleMap, radiusSearchClicked]);

  //Render POI marker as search location in case radiusLatParam && radiusLongParam exist
  useEffect(() => {
    if (googleMap && radiusLatParam && radiusLongParam) {
      const landmarkBase64 = getLandmarkIcon();
      const POIMarker = new google.maps.Marker({
        icon: {
          url: `data:image/svg+xml;base64,${landmarkBase64}`,
        },
        optimized: true,
        position: new google.maps.LatLng({
          lat: parseFloat(radiusLatParam),
          lng: parseFloat(radiusLongParam),
        }),
        animation: google.maps.Animation.BOUNCE,
        zIndex: google.maps.Marker.MAX_ZINDEX - 1,
      });

      POIMarker.setMap(googleMap);
    }
  }, [googleMap]);

  useEffect(() => {
    return () => {
      const controlButton = document.getElementById('map-control-button');

      if (controlButton) {
        controlButton.style.visibility = 'hidden';
      }
    };
  }, [hotelMetaSearch.search?.id]);

  useEffect(() => {
    if (radiusSearchClicked && previousPOIMarkers.length) {
      previousPOIMarkers.forEach((marker) => marker.setMap(null));
    }
  }, [radiusSearchClicked, previousPOIMarkers]);

  const handlePOIChipNavigate = (type: 'prev' | 'next') => {
    landmarksRef.current?.scrollBy({
      left: (type === 'prev' && isRtl) || (type === 'next' && !isRtl) ? 200 : -200,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (hotelMetaSearch.landmarks !== undefined) {
      setLandmarks(hotelMetaSearch.landmarks);
    }
  }, [hotelMetaSearch.landmarks]);

  useEffect(() => {
    if (!!hotelMetaSearch.search?.id && hotelMetaSearch.search?.searchType !== 'GEO_LOCATION') {
      const { id } = hotelMetaSearch.search;
      setOriginalSearchId(id);
      setSelectedPOI({});
    }
  }, [hotelMetaSearch.search?.id, hotelMetaSearch.search?.searchType]);

  useEffect(() => {
    if (previousHotelMarkers.length && !isLoadingCitySearch) {
      keepMapViewport.current = true;
    }
  }, [currency, previousHotelMarkers.length, isLoadingCitySearch]);

  useEffect(() => {
    const updateButtonsVisibility = () => {
      const scrollLeft = landmarksRef.current?.scrollLeft || 0;
      setLandmarkScroll(isRtl ? -scrollLeft : scrollLeft);
    };
    landmarksRef.current?.addEventListener('scroll', updateButtonsVisibility);

    return () => landmarksRef.current?.removeEventListener('scroll', updateButtonsVisibility);
  }, [landmarksRef.current]);

  useEffect(() => {
    if (landmarks.length) {
      const interval = setInterval(() => {
        setIndexDisplayPlaceholder((prevIndex) => (prevIndex + 1) % landmarks.length);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [landmarks.length]);

  return (
    <>
      {googleMap && previousHotelMarkers.length ? (
        <>
          {isHalfScreenMap ? (
            <div
              className={styles.mapFullscreen}
              onClick={() => setMapFullscreen && setMapFullscreen(!isMapFullscreen)}
            >
              <DoubleArrowRightIcon
                width={24}
                height={24}
                className={clsx(isMapFullscreen && styles.expanded)}
              />
            </div>
          ) : null}
          <div className={styles.searchButton}>
            {landmarks.length ? (
              <div className={styles.poiList}>
                <div className={styles.scrollingBar} ref={landmarksRef}>
                  <button
                    type='button'
                    className={clsx(styles.prevButton, landmarkScroll <= 0 && styles.hidden)}
                    onClick={() => handlePOIChipNavigate('prev')}
                  >
                    <ChevronLeft className={styles.chevron} />
                  </button>

                  {landmarks.map((item) => (
                    <div
                      key={item.id}
                      className={clsx(
                        styles.poiItem,
                        selectedPOI.name && selectedPOI.name === item.name && styles.activePOI,
                      )}
                      onClick={() => {
                        setDestinationSearchQuery('');
                        handlePoiClick(item.latitude, item.longitude, item.name);
                      }}
                      title={item.name}
                    >
                      <svg viewBox='0 0 17 16'>
                        <path
                          fillRule='evenodd'
                          d='M2.88507 13.6686C2.55035 13.6686 2.0282 13.3508 2.0282 12.9609V12.8057C2.0282 12.4158 2.29954 12.0998 2.63426 12.0998L3.66832 12.1016V7.03659L2.63426 7.03303C2.28247 7.03303 1.97158 6.78514 1.86824 6.42063C1.77306 6.08461 1.87518 5.72277 2.12078 5.50097L2.18514 5.44854L8.05016 2.48329C8.18594 2.38375 8.34284 2.3335 8.49973 2.3335C8.62524 2.3335 8.75062 2.36566 8.86526 2.42949L8.94907 2.48329L14.8141 5.44854C15.1057 5.66229 15.2341 6.05661 15.131 6.42063C15.0356 6.7571 14.7973 6.99599 14.4791 7.03047L14.365 7.03303L13.3615 7.03659V12.1016L14.365 12.0998C14.6997 12.0998 14.971 12.4158 14.971 12.8057V12.9609C14.971 13.3508 14.6997 13.6668 14.365 13.6668L2.88507 13.6686ZM5.40006 7.07488C5.40006 6.96443 5.4896 6.87488 5.60006 6.87488H6.49097C6.60143 6.87488 6.69097 6.96443 6.69097 7.07488V11.9016C6.69097 12.012 6.60143 12.1016 6.49097 12.1016H5.60006C5.4896 12.1016 5.40006 12.012 5.40006 11.9016V7.07488ZM7.89097 7.07488C7.89097 6.96443 7.98051 6.87488 8.09097 6.87488H8.98188C9.09233 6.87488 9.18188 6.96443 9.18188 7.07488V11.9016C9.18188 12.012 9.09233 12.1016 8.98188 12.1016H8.09097C7.98051 12.1016 7.89097 12.012 7.89097 11.9016V7.07488ZM10.3819 7.07488C10.3819 6.96443 10.4714 6.87488 10.5819 6.87488H11.4728C11.5832 6.87488 11.6728 6.96443 11.6728 7.07488V11.9016C11.6728 12.012 11.5832 12.1016 11.4728 12.1016H10.5819C10.4714 12.1016 10.3819 12.012 10.3819 11.9016V7.07488Z'
                        />
                      </svg>
                      <span className={styles.poiName}>{item.name}</span>
                    </div>
                  ))}

                  <button
                    type='button'
                    className={clsx(
                      styles.nextButton,
                      landmarkScrollWidth - landmarkScroll <= 1 && styles.hidden,
                    )}
                    onClick={() => handlePOIChipNavigate('next')}
                  >
                    <ChevronRight className={styles.chevron} />
                  </button>
                </div>
              </div>
            ) : null}

            <div
              id='search-button'
              className={clsx(styles.poiItem, styles.searchInput)}
              onClick={handlePoiSearch}
            >
              <SearchIcon /> {translations.search} <span className={styles.placeholder}>|</span>
              <p
                className={clsx(
                  destinationSearchQuery && destinationSearchQuery.length
                    ? styles.searchDestination
                    : styles.placeholder,
                  styles.ellipsis,
                )}
              >
                {destinationSearchQuery || landmarks[indexDisplayPlaceholder]?.name}
              </p>
            </div>
          </div>
          {isStartSearchPOI ? (
            <div className={styles.searchLayout}>
              <div
                className={styles.inputSelectorBackground}
                onClick={handleCloseSearchLayout}
              ></div>
              <div className={styles.inputWrapper}>
                <div className={styles.inputPrefix}>
                  <SearchIcon className={styles.searchIcon} /> {translations.search}{' '}
                  <span className={styles.placeholder}>|</span>
                </div>

                <input
                  ref={placePickerRef}
                  className={styles.inputSelector}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleOnPlacePickerChange(e.target.value)
                  }
                  onKeyDown={handleOnPlacePickerKeyDown}
                  onFocus={handleOnPlacePickerFocus}
                  placeholder={translations.find_nearby_attractions as string}
                  value={destinationSearchQuery}
                ></input>

                {!!destinationSearchQuery && isPlacePickerFocus ? (
                  <button
                    type='button'
                    onClick={handleClearSearchQuery}
                    className={styles.closeBtn}
                  >
                    <ClearIcon className={styles.closeIcon} />
                  </button>
                ) : null}
              </div>

              {!!destinationSearchQuery && destinationSearchQueryPlaces.predictions.length > 0 ? (
                <div className={styles.searchtListWrapper}>
                  {destinationSearchQueryPlaces.predictions.map(
                    (destinationSearchQueryPlace, index) => (
                      <div
                        key={index}
                        className={styles.place}
                        onClick={() => handleOnPlaceSelected(destinationSearchQueryPlace)}
                      >
                        {getPOIDisplay(destinationSearchQueryPlace)}
                      </div>
                    ),
                  )}
                </div>
              ) : null}

              {!!destinationSearchQuery &&
              destinationSearchQueryPlaces.predictions.length === 0 &&
              !selectedPlace ? (
                <div className={styles.searchtListWrapper}>
                  {!isLoading ? (
                    <AlertMessage
                      type={MessageTypes.NO_RESULTS}
                      className={styles.noResults}
                      title={translations.no_results_header_v2 as string}
                      message={''}
                      buttonLabel={''}
                      onClick={() => {}}
                    />
                  ) : (
                    <div className={styles.spinnerWrapper}>
                      <Spinner className={styles.spinner} />
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      <div id='mapV2' ref={mapDivRef} className={className} onClick={handleCloseInfoWindow}>
        <div ref={hotelCardPopUpRef}>
          {!!selectedMarkerHotelViewModel ? (
            <HotelCardMap
              hotelViewModel={selectedMarkerHotelViewModel}
              searchId={hotelMetaSearch.search!.id}
              onHotelCardMapClick={() => onHotelCardMapClick(selectedMarkerHotelViewModel.hotelId)}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
              onMouseLeave={handleCloseInfoWindow}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default HotelMap;
